"use client"

import { Box, Heading, Text, SimpleGrid, Flex, useColorModeValue } from "@chakra-ui/react"
import { motion, useInView } from "framer-motion"
import { useRef } from "react"
import { UserPlusIcon, MagnifyingGlassIcon, LockOpenIcon, ArrowRightIcon } from "@heroicons/react/24/outline"

const MotionBox = motion(Box)
const MotionFlex = motion(Flex)
const MotionHeading = motion(Heading)
const MotionText = motion(Text)

const steps = [
    {
        title: "Register an Account",
        description:
            "Create your employer profile in minutes. Add your company details and start building your employer brand on our platform.",
        icon: <UserPlusIcon className="w-10 h-10" />,
        color: "blue",
    },
    {
        title: "Search for Candidates",
        description:
            "Use our powerful search tools to find candidates that match your specific requirements. Filter by skills, experience, location, and more.",
        icon: <MagnifyingGlassIcon className="w-10 h-10" />,
        color: "purple",
    },
    {
        title: "Unlock the Database",
        description:
            "Get full access to candidate profiles, contact information, and our suite of hiring tools to streamline your recruitment process.",
        icon: <LockOpenIcon className="w-10 h-10" />,
        color: "cyan",
    },
]

const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" },
    },
}

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
        },
    },
}

const itemVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: "easeOut" },
    },
}

const cardHover = {
    hover: {
        y: -10,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        transition: {
            type: "spring",
            stiffness: 300,
            damping: 10
        }
    }
}

export default function StepsSection() {
    const ref = useRef(null)
    const isInView = useInView(ref, { once: true, amount: 0.1 })

    const bgSecondary = useColorModeValue("gray.50", "gray.800")
    const bgPrimary = useColorModeValue("white", "gray.900")
    const textPrimary = useColorModeValue("gray.800", "white")
    const textSecondary = useColorModeValue("gray.600", "gray.300")

    return (
        <MotionBox
            as="section"
            py={{ base: 16, md: 24 }}
            bg={bgSecondary}
            position="relative"
            overflow="hidden"
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeIn}
        >
            {/* Background decorative elements */}
            <MotionBox
                position="absolute"
                top="10%"
                right="-5%"
                width="300px"
                height="300px"
                borderRadius="full"
                bg="indigo.500"
                opacity="0.05"
                zIndex="0"
                animate={{
                    y: [0, -20, 0],
                }}
                transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
            />
            <MotionBox
                position="absolute"
                bottom="10%"
                left="-5%"
                width="250px"
                height="250px"
                borderRadius="full"
                bg="purple.200"
                opacity="0.05"
                zIndex="0"
                animate={{
                    y: [0, 20, 0],
                }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                }}
            />

            <Box px={{ base: 6, md: 12, lg: 20 }} mx="auto" w="full" maxW="none" position="relative" zIndex="1">
                <MotionBox
                    ref={ref}
                    textAlign="center"
                    mb={16}
                    variants={fadeIn}
                >
                    <MotionHeading
                        as="h2"
                        fontSize={{ base: "3xl", md: "4xl" }}
                        fontWeight="bold"
                        color={textPrimary}
                        mb={4}
                        variants={fadeIn}
                    >
                        Find the Perfect Candidate in{" "}
                        <Text
                            as="span"
                            className="bg-gradient-to-r from-indigo-500 to-purple-500 text-transparent bg-clip-text inline"
                        >
                            Three Simple Steps
                        </Text>
                    </MotionHeading>
                    <MotionText
                        fontSize={{ base: "lg", md: "xl" }}
                        color={textSecondary}
                        maxW="3xl"
                        mx="auto"
                        variants={fadeIn}
                    >
                        Our streamlined process makes hiring top talent faster and easier than ever before.
                    </MotionText>
                </MotionBox>

                <MotionBox
                    variants={staggerContainer}
                >
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10}>
                        {steps.map((step, index) => (
                            <MotionBox
                                key={index}
                                variants={itemVariant}
                                bg={bgPrimary}
                                borderRadius="lg"
                                boxShadow="lg"
                                p={8}
                                textAlign="center"
                                position="relative"
                                whileHover="hover"
                                // variants={itemVariant}
                                custom={index}
                                initial="hidden"
                                animate={isInView ? "visible" : "hidden"}
                            >
                                {/* Step number */}
                                <MotionBox
                                    position="absolute"
                                    top="-15px"
                                    left="50%"
                                    transform="translateX(-50%)"
                                    bg={`${step.color}.500`}
                                    color="white"
                                    borderRadius="full"
                                    width="30px"
                                    height="30px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    fontWeight="bold"
                                    fontSize="sm"
                                    boxShadow="md"
                                    whileHover={{ scale: 1.1 }}
                                >
                                    {index + 1}
                                </MotionBox>

                                <MotionFlex
                                    justify="center"
                                    align="center"
                                    bg={`${step.color}.50`}
                                    color={`${step.color}.500`}
                                    w={20}
                                    h={20}
                                    borderRadius="full"
                                    mb={6}
                                    mx="auto"
                                    animate={{
                                        scale: [1, 1.05, 1],
                                    }}
                                    transition={{
                                        duration: 4,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                        delay: index * 0.5
                                    }}
                                >
                                    {step.icon}
                                </MotionFlex>
                                <MotionHeading
                                    as="h3"
                                    fontSize={{ base: "xl", md: "2xl" }}
                                    fontWeight="bold"
                                    color={textPrimary}
                                    mb={4}
                                    whileHover={{ color: `${step.color}.500` }}
                                >
                                    {step.title}
                                </MotionHeading>
                                <MotionText color={textSecondary} whileHover={{ scale: 1.02 }}>
                                    {step.description}
                                </MotionText>

                                {/* Arrow connecting steps */}
                                {index < steps.length - 1 && (
                                    <MotionBox
                                        display={{ base: "none", md: "block" }}
                                        position="absolute"
                                        right="-30px"
                                        top="50%"
                                        transform="translateY(-50%)"
                                        zIndex="1"
                                        color={`${step.color}.300`}
                                        animate={{
                                            x: [0, 5, 0]
                                        }}
                                        transition={{
                                            duration: 3,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                            delay: index * 0.3
                                        }}
                                    >
                                        <ArrowRightIcon className="w-6 h-6" />
                                    </MotionBox>
                                )}
                            </MotionBox>
                        ))}
                    </SimpleGrid>
                </MotionBox>
            </Box>
        </MotionBox>
    )
}